/**
 * components/KpiCard
 * ──────────────────────────────────────────────────────────────────────────
 * One Overview / approver-queue KPI card: a 3px accent strip, then a 7px
 * status dot beside an 11px uppercase label, a 22px mono value, and an 11.5px
 * grey sub line.
 *
 * The `sub` line is where invariant 4 becomes visible to the user. There is no
 * single "total cash in custody" figure in this product, because there is no
 * honest way to produce one without an exchange rate and a date stamp. So the
 * LARGEST currency is the 22px value and the others go on the sub line as
 * "+ INR 12,400.00" — the caller composes that with lib/money `splitTotals`
 * and `formatRest`.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { card as cardSpace } from '../../theme/spacing';
import { mono, numeric, sans, size as fontSize } from '../../theme/typography';
import { pointerCursor } from '../../theme/webStyle';
import { type ToneName, toneOf } from '../../lib/status';
import Dot from '../Dot';

export interface KpiCardProps {
  label: string;
  /** Pre-formatted — usually via lib/money `format`, or a plain count. */
  value: string;
  /** The 11.5px line beneath: other currencies, an age, an oldest timestamp. */
  sub?: string;
  /** The 3px strip. Overview: ink / amber / red / orange, in card order. */
  accent: string;
  /** The dot beside the label. Defaults to the accent colour. */
  tone?: ToneName;
  /** Makes the whole card a link to the screen that resolves it. */
  onPress?: () => void;
}

export function KpiCard({ label, value, sub, accent, tone, onPress }: KpiCardProps) {
  const dotColor = tone ? toneOf(tone).dot : accent;

  return (
    <Pressable
      onPress={onPress}
      disabled={!onPress}
      accessibilityRole={onPress ? 'button' : undefined}
      accessibilityLabel={`${label}: ${value}`}
      style={[styles.card, !!onPress && pointerCursor]}
    >
      <View style={[styles.accent, { backgroundColor: accent }]} />
      <View style={styles.body}>
        <View style={styles.labelRow}>
          <Dot color={dotColor} size={7} />
          <Text numberOfLines={1} style={styles.label}>
            {label}
          </Text>
        </View>
        <Text numberOfLines={1} style={styles.value}>
          {value}
        </Text>
        {!!sub && (
          <Text numberOfLines={2} style={styles.sub}>
            {sub}
          </Text>
        )}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderColor: neutral.border,
    borderRadius: radii.cardBlock,
    backgroundColor: neutral.surface,
    overflow: 'hidden',
  },
  accent: { height: cardSpace.accentStripHeight },
  // 15 top / 17 sides / 17 bottom, from the design source.
  body: { paddingTop: 15, paddingHorizontal: 17, paddingBottom: 17 },
  labelRow: { flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 9 },
  label: {
    ...sans('400'),
    fontSize: fontSize.metaSm,
    letterSpacing: 0.66,
    textTransform: 'uppercase',
    color: neutral.grey600,
    flexShrink: 1,
  },
  value: { ...mono('500'), fontSize: fontSize.kpi, ...numeric, color: neutral.ink },
  sub: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey400, marginTop: 5 },
});

export default KpiCard;
